import React, { useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler } from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

export function LineChartGradient({
	labels = ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep'],
	data = [3500, 4000, 4500, 4200, 4700, 4800],
	label = 'Trend',
}) {
	// Track theme so memos re-run on theme switch
	const [isDarkMode, setIsDarkMode] = React.useState(() => document.body.classList.contains('dark-theme'));

	React.useEffect(() => {
		const observer = new MutationObserver(() => {
			setIsDarkMode(document.body.classList.contains('dark-theme'));
		});
		observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
		return () => observer.disconnect();
	}, []);

	// Memoize the chart data
	const renderData = useMemo(
		() => {
			const primaryColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-color').trim() || '#4d7cfe';
			const primaryHover = getComputedStyle(document.documentElement).getPropertyValue('--primary-color-hover').trim() || '#4d69eb';

			return {
				labels,
				datasets: [
					{
						label,
						data,
						fill: 'start',
						backgroundColor: (context) => {
							const ctx = context.chart.ctx;
							const gradient = ctx.createLinearGradient(0, 0, 0, 200);
							
							if (isDarkMode) {
								// Very soft, non-straining colors for dark mode
								gradient.addColorStop(0, 'rgba(80, 140, 240, 0.25)');
								gradient.addColorStop(1, 'rgba(80, 140, 240, 0.02)');
							} else {
								gradient.addColorStop(0, primaryColor);
								gradient.addColorStop(1, 'rgba(253, 253, 253, 0)');
							}
							return gradient;
						},
						borderColor: isDarkMode ? 'rgba(100, 160, 240, 0.5)' : primaryHover,
						borderWidth: isDarkMode ? 2.5 : 2,
						tension: 0.35,
						pointRadius: isDarkMode ? 3 : 4,
						pointHoverRadius: isDarkMode ? 4 : 5,
						pointBackgroundColor: isDarkMode ? 'rgba(80, 140, 240, 0.6)' : primaryHover,
						pointBorderColor: isDarkMode ? 'rgba(80, 140, 240, 0.8)' : primaryColor,
						pointBorderWidth: isDarkMode ? 1 : 2,
					},
				],
			};
		},
		[labels, data, isDarkMode, label]
	);

	// Memoize the chart options
	const options = useMemo(
		() => {
			const textColor = isDarkMode ? '#a8a8a8' : '#666666';
			const gridColor = isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.05)';
			// Tooltip background is always dark — text must always be light
			const tooltipBg = isDarkMode ? 'rgba(26, 26, 26, 0.95)' : 'rgba(30, 30, 40, 0.88)';
			const tooltipBorder = isDarkMode ? 'rgba(100, 160, 240, 0.3)' : 'rgba(77, 124, 254, 0.35)';

			return {
				maintainAspectRatio: false,
				responsive: true,
				plugins: {
					legend: {
						display: false,
					},
					tooltip: {
						backgroundColor: tooltipBg,
						borderColor: tooltipBorder,
						borderWidth: 1,
						// Always white/light — tooltip bg is always dark
						titleColor: '#ffffff',
						bodyColor: 'rgba(255, 255, 255, 0.75)',
						titleFont: {
							size: 12,
							weight: 'bold',
						},
						bodyFont: {
							size: 12,
						},
						padding: 10,
						displayColors: false,
					},
				},
				scales: {
					x: {
						grid: {
							display: false,
						},
						ticks: {
							font: {
								size: 13,
							},
							color: textColor,
						},
					},
					y: {
						grid: {
							color: gridColor,
							drawBorder: false,
						},
						ticks: {
							callback: (value) => '₹' + value,
							font: {
								size: 13,
								weight: 'bold',
							},
							color: textColor,
						},
					},
				},
				elements: {
					line: {
						tension: 0.35,
					},
				},
				interaction: {
					intersect: true,
				},
			};
		},
		[isDarkMode]
	);

	return <Line data={renderData} options={options} height={400} width="100%" />;
}